import React, { useState } from "react";
import { useQuery } from "react-query";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import authApi from "../../../service/IdApi";
import PageLoading from "../../../components/PageLoading";
import PageLoadError from "../../../components/PageLoadError";
import PageHeading from "../../../components/PageHeading";

const DATE_OPTIONS = [
    { label: "Any date", value: "" },
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Last 7 days", value: "last7" },
    { label: "Last 30 days", value: "last30" },
];

const startOfLocalDay = (date) => {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
};

const resolveCreatedDateRange = (preset) => {
    if (!preset) {
        return null;
    }
    const todayStart = startOfLocalDay(new Date());
    const dayMs = 24 * 60 * 60 * 1000;
    if (preset === "today") {
        return {
            createdFrom: todayStart.getTime(),
            createdTo: todayStart.getTime() + dayMs,
        };
    }
    if (preset === "yesterday") {
        return {
            createdFrom: todayStart.getTime() - dayMs,
            createdTo: todayStart.getTime(),
        };
    }
    if (preset === "last7") {
        return {
            createdFrom: todayStart.getTime() - (6 * dayMs),
            createdTo: todayStart.getTime() + dayMs,
        };
    }
    if (preset === "last30") {
        return {
            createdFrom: todayStart.getTime() - (29 * dayMs),
            createdTo: todayStart.getTime() + dayMs,
        };
    }
    return null;
};

const formatCostUsd = (value) => {
    if (value == null || value === "" || Number.isNaN(Number(value))) {
        return "$0.0000";
    }
    return `$${Number(value).toFixed(4)}`;
};

const AiCost = () => {
    const [items, setItems] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [date, setDate] = useState("last7");

    const { isLoading, isFetching, isError, refetch } = useQuery(
        ["AiCost", date],
        async () => {
            const searchBody = {};
            const dateRange = resolveCreatedDateRange(date);
            if (dateRange) {
                searchBody.createdFrom = dateRange.createdFrom;
                searchBody.createdTo = dateRange.createdTo;
            }
            const result = await authApi.post("admin/ai-cost/summary", searchBody);
            setItems(result.data.items || []);
            setLoaded(true);
            return result.data;
        }
    );

    const pageHeader = (
        <PageHeading pageTitle="Admin - AI Cost">
            <div className="block" />
        </PageHeading>
    );

    if (isLoading && !loaded) {
        return (
            <div>
                {pageHeader}
                <PageLoading />
            </div>
        );
    }

    if (isError) {
        return (
            <div>
                {pageHeader}
                <PageLoadError />
            </div>
        );
    }

    const onDateChange = (event) => {
        const value = Object.prototype.hasOwnProperty.call(event, "value") ? event.value : "";
        setDate(value == null ? "" : value);
    };

    const costBody = (row) => formatCostUsd(row.costUsd);

    let sumCostUsd = 0;
    for (let i = 0; i < items.length; i++) {
        const value = items[i] && items[i].costUsd;
        if (value == null || Number.isNaN(Number(value))) {
            continue;
        }
        sumCostUsd += Number(value);
    }

    return (
        <div>
            {pageHeader}
            <div className="layout-content">
                <div className="card pt-3 sm:pt-0">
                    <div className="flex flex-wrap align-items-center gap-2 py-3">
                        <div className="font-bold white-space-nowrap">Filter by</div>
                        <Dropdown
                            value={date}
                            options={DATE_OPTIONS}
                            onChange={onDateChange}
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Date"
                            aria-label="Filter by date"
                        />
                        <Button
                            icon="pi pi-refresh"
                            className="p-button-outlined"
                            onClick={() => refetch()}
                            loading={isFetching}
                            aria-label="Refresh"
                            tooltip="Refresh"
                            tooltipOptions={{ position: "top" }}
                        />
                    </div>
                    <DataTable
                        value={items}
                        dataKey="type"
                        className="datatable-responsive"
                        emptyMessage="No AI cost data found."
                        loading={isLoading || isFetching}
                    >
                        <Column
                            field="type"
                            header="Type"
                            footer="Total"
                            footerClassName="font-bold"
                        />
                        <Column
                            field="costUsd"
                            header="Cost"
                            body={costBody}
                            footer={formatCostUsd(sumCostUsd)}
                            className="text-right"
                            headerClassName="right-aligned-header"
                            footerClassName="right-aligned-header font-bold"
                        />
                    </DataTable>
                </div>
            </div>
        </div>
    );
};

export default AiCost;
